import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import MasterLayout from "../otherImages/MasterLayout";
import Swal from "sweetalert2";
import { logoutUser } from "../utils/authUtils";

const ContactFormEntries = () => {
  const BASE_URL = "https://clearshine.koderspedia.online";

  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getToken = () => localStorage.getItem("token");

  const getHeaders = () => {
    const token = getToken();

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const getContactEntries = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/get-all-contact`, {
        method: "GET",
        headers: getHeaders(),
      });

      // Handle token expiration (401 Unauthorized)
      if (response.status === 401) {
        console.warn("Token expired - Logging out user");
        Swal.fire({
          icon: "warning",
          title: "Session Expired",
          text: "Your session has expired. Please login again.",
          confirmButtonColor: "#0CAFE6",
        }).then(() => {
          logoutUser();
        });
        return;
      }

      const result = await response.json();

      console.log("Contact Form API Response:", result);

      if (response.ok && result?.success) {
        setEntries(result?.data || []);
        setCurrentPage(1);
      } else {
        setEntries([]);

        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: result?.message || "Failed to fetch contact form entries",
          confirmButtonColor: "#0CAFE6",
        });
      }
    } catch (error) {
      console.error("Contact Entries Error:", error);

      setEntries([]);

      Swal.fire({
        icon: "error",
        title: "Something Went Wrong!",
        text: "Something went wrong while fetching contact form entries.",
        confirmButtonColor: "#0CAFE6",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!entry?.id) {
      Swal.fire({
        icon: "error",
        title: "Entry ID Missing",
        text: "Contact entry ID not found.",
        confirmButtonColor: "#0CAFE6",
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Delete Contact Entry?",
      text: `Are you sure you want to delete entry #${entry.id}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!confirmResult.isConfirmed) return;

    try {
      Swal.fire({
        title: "Deleting...",
        text: "Please wait while contact entry is being deleted.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await fetch(`${BASE_URL}/deleteContactEntry/${entry.id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      // Handle token expiration (401 Unauthorized)
      if (response.status === 401) {
        console.warn("Token expired - Logging out user");
        Swal.fire({
          icon: "warning",
          title: "Session Expired",
          text: "Your session has expired. Please login again.",
          confirmButtonColor: "#0CAFE6",
        }).then(() => {
          logoutUser();
        });
        return;
      }

      const result = await response.json();

      console.log("Delete Contact Entry Response:", result);

      if (response.ok && result?.success) {
        setEntries((prev) => {
          const updatedEntries = prev.filter((item) => item.id !== entry.id);

          const updatedFilteredLength = updatedEntries.filter((item) => {
            const searchText = search.toLowerCase();
            const fullName = `${item.firstName || ""} ${
              item.lastName || ""
            }`.toLowerCase();

            return (
              fullName.includes(searchText) ||
              String(item.email || "").toLowerCase().includes(searchText) ||
              String(item.phone || "").toLowerCase().includes(searchText) ||
              String(item.message || "").toLowerCase().includes(searchText) ||
              String(item.createdAt || "").toLowerCase().includes(searchText)
            );
          }).length;

          const updatedTotalPages =
            Math.ceil(updatedFilteredLength / rowsPerPage) || 1;

          if (currentPage > updatedTotalPages) {
            setCurrentPage(updatedTotalPages);
          }

          return updatedEntries;
        });

        if (selectedEntry?.id === entry.id) {
          setSelectedEntry(null);
        }

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: result?.message || "Contact entry deleted successfully.",
          confirmButtonColor: "#0CAFE6",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: result?.message || "Failed to delete contact entry.",
          confirmButtonColor: "#0CAFE6",
        });
      }
    } catch (error) {
      console.error("Delete Contact Entry Error:", error);

      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        text: "Something went wrong while deleting contact entry.",
        confirmButtonColor: "#0CAFE6",
      });
    }
  };

  useEffect(() => {
    getContactEntries();
  }, []);

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filteredEntries = useMemo(() => {
    const searchText = search.toLowerCase();

    return entries.filter((item) => {
      const fullName = `${item.firstName || ""} ${
        item.lastName || ""
      }`.toLowerCase();

      return (
        fullName.includes(searchText) ||
        String(item.email || "").toLowerCase().includes(searchText) ||
        String(item.phone || "").toLowerCase().includes(searchText) ||
        String(item.message || "").toLowerCase().includes(searchText) ||
        String(item.createdAt || "").toLowerCase().includes(searchText)
      );
    });
  }, [entries, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.ceil(filteredEntries.length / rowsPerPage) || 1;

  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return filteredEntries.slice(startIndex, endIndex);
  }, [filteredEntries, currentPage, rowsPerPage]);

  const paginationStart =
    filteredEntries.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;

  const paginationEnd = Math.min(
    currentPage * rowsPerPage,
    filteredEntries.length
  );

  const getFullName = (entry) => {
    const firstName = entry?.firstName || "";
    const lastName = entry?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || "-";
  };

  const tableCellStyle = {
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <MasterLayout>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h4 className="fw-bold mb-8">Contact Form</h4>
          <p className="text-secondary-light mb-0">
            View and manage contact form submissions.
          </p>
        </div>

        {/* <button
          type="button"
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          onClick={getContactEntries}
        >
          <Icon icon="mdi:refresh" width="20" height="20" />
          Refresh
        </button> */}
      </div>

      <div className="row gy-4 mb-24">
        <div className="col-xxl-4 col-sm-6">
          <div className="card p-20 radius-12 border-0 shadow-sm h-100">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div style={{ minWidth: 0 }}>
                <p className="text-secondary-light mb-8">Total Entries</p>
                <h4 className="mb-0">{entries.length}</h4>
              </div>

              <div className="w-48-px h-48-px rounded-circle bg-primary-50 d-flex justify-content-center align-items-center flex-shrink-0">
                <Icon icon="solar:letter-linear" width="26" height="26" />
              </div>
            </div>
          </div>
        </div>

        <div className="col-xxl-4 col-sm-6">
          <div className="card p-20 radius-12 border-0 shadow-sm h-100">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div style={{ minWidth: 0, width: "100%" }}>
                <p className="text-secondary-light mb-8">Latest Entry</p>
                <h4
                  className="mb-0"
                  title={getFullName(entries[0])}
                  style={{
                    fontSize: "24px",
                    lineHeight: "1.25",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {getFullName(entries[0])}
                </h4>
              </div>

              <div className="w-48-px h-48-px rounded-circle bg-info-focus d-flex justify-content-center align-items-center flex-shrink-0">
                <Icon icon="mdi:account-clock-outline" width="26" height="26" />
              </div>
            </div>
          </div>
        </div>

        <div className="col-xxl-4 col-sm-6">
          <div className="card p-20 radius-12 border-0 shadow-sm h-100">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div style={{ minWidth: 0 }}>
                <p className="text-secondary-light mb-8">Last Date</p>
                <h4
                  className="mb-0"
                  style={{
                    fontSize: "24px",
                    lineHeight: "1.25",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDate(entries[0]?.createdAt)}
                </h4>
              </div>

              <div className="w-48-px h-48-px rounded-circle bg-warning-focus d-flex justify-content-center align-items-center flex-shrink-0">
                <Icon
                  icon="mdi:calendar-clock-outline"
                  width="26"
                  height="26"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card basic-data-table">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
          <h5 className="card-title mb-0">Contact Form Entries</h5>

          <div
            className="navbar-search"
            style={{ maxWidth: "420px", width: "100%" }}
          >
            <input
              type="text"
              name="search"
              placeholder="Search contact entry"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </div>
        </div>

        <div className="card-body">
          <div
            className="table-responsive"
            style={{
              overflowX: "hidden",
              overflowY: "visible",
            }}
          >
            <table
              className="table bordered-table mb-0"
              style={{
                width: "100%",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "60px" }} />
                <col style={{ width: "300px" }} />
                <col style={{ width: "285px" }} />
                <col style={{ width: "155px" }} />
                <col style={{ width: "135px" }} />
                <col style={{ width: "120px" }} />
              </colgroup>

              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Created At</th>
                  <th className="text-center">Manage</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-24">
                      Loading contact entries...
                    </td>
                  </tr>
                ) : filteredEntries.length > 0 ? (
                  paginatedEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td style={tableCellStyle}>{entry.id}</td>

                      <td style={tableCellStyle}>
                        <div
                          className="d-flex align-items-center gap-3"
                          style={{ minWidth: 0 }}
                        >
                          <div className="w-40-px h-40-px rounded-circle bg-primary-50 d-flex justify-content-center align-items-center flex-shrink-0">
                            <Icon
                              icon="solar:user-linear"
                              width="22"
                              height="22"
                            />
                          </div>

                          <h6
                            className="text-md mb-0"
                            title={getFullName(entry)}
                            style={{
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {getFullName(entry)}
                          </h6>
                        </div>
                      </td>

                      <td style={tableCellStyle} title={entry.email || "-"}>
                        {entry.email || "-"}
                      </td>

                      <td style={tableCellStyle} title={entry.phone || "-"}>
                        {entry.phone || "-"}
                      </td>

                      <td style={tableCellStyle}>
                        {formatDate(entry.createdAt)}
                      </td>

                      <td style={tableCellStyle}>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            type="button"
                            className="w-36-px h-36-px bg-primary-50 text-primary-600 rounded-circle d-flex justify-content-center align-items-center border-0"
                            onClick={() => setSelectedEntry(entry)}
                            title="View"
                          >
                            <Icon
                              icon="solar:eye-outline"
                              width="20"
                              height="20"
                            />
                          </button>

                          <button
                            type="button"
                            className="w-36-px h-36-px bg-danger-focus text-danger-main rounded-circle d-flex justify-content-center align-items-center border-0"
                            onClick={() => handleDeleteEntry(entry)}
                            title="Delete"
                          >
                            <Icon
                              icon="material-symbols:delete-outline"
                              width="20"
                              height="20"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-24">
                      No contact form entries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredEntries.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "24px",
                padding: "18px 8px 0",
                fontSize: "14px",
                color: "#111827",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>Rows per page:</span>

                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    border: "0",
                    outline: "0",
                    background: "transparent",
                    fontSize: "14px",
                    color: "#111827",
                    cursor: "pointer",
                    padding: "2px 4px",
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <span>
                {paginationStart}-{paginationEnd} of {filteredEntries.length}
              </span>

              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage((prev) => Math.max(prev - 1, 1))
                }
                style={{
                  border: "0",
                  background: "transparent",
                  padding: "0",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  opacity: currentPage === 1 ? 0.4 : 1,
                  display: "flex",
                  alignItems: "center",
                  color: "#111827",
                }}
              >
                <Icon icon="mdi:chevron-left" width="24" height="24" />
              </button>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                style={{
                  border: "0",
                  background: "transparent",
                  padding: "0",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                  opacity: currentPage === totalPages ? 0.4 : 1,
                  display: "flex",
                  alignItems: "center",
                  color: "#111827",
                }}
              >
                <Icon icon="mdi:chevron-right" width="24" height="24" />
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedEntry && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.45)",
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-12">
              <div className="modal-header">
                <h5 className="modal-title">Contact Form Details</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedEntry(null)}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <strong>First Name:</strong>
                  <p className="mb-0">{selectedEntry.firstName || "-"}</p>
                </div>

                <div className="mb-3">
                  <strong>Last Name:</strong>
                  <p className="mb-0">{selectedEntry.lastName || "-"}</p>
                </div>

                <div className="mb-3">
                  <strong>Email:</strong>
                  <p className="mb-0">{selectedEntry.email || "-"}</p>
                </div>

                <div className="mb-3">
                  <strong>Phone Number:</strong>
                  <p className="mb-0">{selectedEntry.phone || "-"}</p>
                </div>

                <div className="mb-3">
                  <strong>Message:</strong>
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "12px 14px",
                      background: "#f8f9fa",
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      maxHeight: "260px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: "1.6",
                    }}
                  >
                    {selectedEntry.message || "-"}
                  </div>
                </div>

                <div className="mb-0">
                  <strong>Created At:</strong>
                  <p className="mb-0">{formatDate(selectedEntry.createdAt)}</p>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedEntry(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteEntry(selectedEntry)}
                >
                  Delete Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default ContactFormEntries;